import {
  parseWithCustomerConfigMessage,
  refineWalletBalanceMode,
  WalletFeatureFieldsObjectSchema,
  WidgetAppearanceSchema,
} from "@blockdaemon/widget-config";
import { PublicKey } from "@solana/web3.js";
import { isAddress } from "viem";
import { z } from "zod";

import { DEFI_ASSETS, STAKING_ASSETS, SWAP_ASSETS } from "../../../shared/utils/asset-config";

export { WidgetAppearanceSchema };

/**
 * Validates a chain identifier in CAIP-2 format.
 * Requires namespace:reference format (e.g., "eip155:1", "solana:5eykt4UsFv8P8JJd4ESu47Z26wfmJSqeLZyqLdqyHi2K").
 */
const validateChainId = (value: string): boolean => {
  // CAIP-2 format: namespace:reference
  // Examples: "eip155:1", "eip155:42161", "solana:5eykt4UsFv8P8JJd4ESu47Z26wfmJSqeLZyqLdqyHi2K", "cosmos:cosmoshub-4"
  return /^[a-z0-9]+:[a-zA-Z0-9_-]+$/.test(value);
};

/**
 * Validates Ethereum address checksum format
 * Uses EIP-55 checksum validation via viem
 */
const validateEthereumAddress = (address: string): boolean => {
  try {
    // viem isAddress function validates both format and EIP-55 checksum
    return isAddress(address);
  } catch {
    return false;
  }
};

/**
 * Validates a Solana vote pubkey (base58 encoded, 32 bytes)
 * Uses @solana/web3.js PublicKey for validation
 */
const validateSolanaVotePubKey = (pubkey: string): boolean => {
  try {
    if (!pubkey || pubkey.trim() === "") {
      return false;
    }
    // PublicKey constructor validates base58 format and 32-byte length
    const pk = new PublicKey(pubkey);
    // Additional check: ensure the pubkey is on the ed25519 curve (valid address)
    return PublicKey.isOnCurve(pk.toBytes());
  } catch {
    return false;
  }
};

/**
 * Zod schema for chain identifier in CAIP-2 format
 * Used for all chain ID fields in config (assets.allowList/blockList, chains.allowList/blockList).
 */
const ChainIdSchema = z
  .string()
  .describe(
    "CAIP-2 chain identifier (namespace:reference, e.g. eip155:1, eip155:42161, solana:5eykt4UsFv8P8JJd4ESu47Z26wfmJSqeLZyqLdqyHi2K)",
  )
  .refine(validateChainId, {
    message:
      "Must be a valid CAIP-2 chain identifier (e.g., 'eip155:1', 'eip155:42161', 'solana:5eykt4UsFv8P8JJd4ESu47Z26wfmJSqeLZyqLdqyHi2K', 'cosmos:cosmoshub-4')",
  });

/**
 * Zod schema for Ethereum address with checksum validation
 */
const EthereumAddressSchema = z.string().refine(validateEthereumAddress, {
  message: "Must be a valid Ethereum address with proper EIP-55 checksum",
});

/**
 * Zod schema for protocol configuration
 */
const LendBorrowMarkets = z.array(
  z.enum([
    "1200", // Aave V3 Ethereum
    "1202", // Aave V3 Testnet Sepolia
    "1302", // Compound V3 Testnet
    "1005", // Aave V2 Polygon
    "1205", // Aave V3 Polygon
    "1204", // Aave V3 Arbitrum
    "1206", // Aave V3 Base
    "1400", // Morpho Ethereum
    "1401", // Morpho Arbitrum
    "1402", // Morpho Base
    "1207", // Aave V3 Optimism
    "1403", // Morpho Optimism
  ]),
);

/**
 * Chain ID format handling:
 * - Config validation requires CAIP-2 format (e.g., "eip155:1", "solana:5eykt4UsFv8P8JJd4ESu47Z26wfmJSqeLZyqLdqyHi2K")
 * - All chain IDs must follow the namespace:reference pattern
 */

/**
 * Zod schema for widget features configuration
 */
/**
 * Base feature toggle schema: always an object with at least { enabled: boolean }.
 * Use loose() to allow forward-compatible extra fields without breaking validation.
 */
const BaseFeatureSchema = z
  .object({
    enabled: z.boolean(),
  })
  .strict();

/**
 * Asset configuration with chain ID and address
 */
const AssetConfigSchema = z.object({
  chainId: ChainIdSchema,
  address: EthereumAddressSchema,
});

/**
 * Shared schema for allow/block list filters
 */
const AllowBlockListSchema = z.object({
  allowList: z.array(z.string()).default([]),
  blockList: z.array(z.string()).default([]),
});

/**
 * Chains allow/block lists for CAIP-2 chain IDs
 */
const ChainAllowBlockListSchema = z.object({
  allowList: z.array(ChainIdSchema).default([]),
  blockList: z.array(ChainIdSchema).default([]),
});

/**
 * Helper to get supported chain IDs from asset configs
 */
function getSupportedChainIds(assets: typeof DEFI_ASSETS): string[] {
  const chainIds = assets.flatMap((asset) =>
    asset.networks.map((network) => String(network.caipNetworkId)),
  );
  return Array.from(new Set(chainIds));
}

/**
 * Helper to create chain validation schema for a specific module
 * Validates that chain IDs in allowList are supported by the module's assets
 */
function createModuleChainSchema(
  moduleAssets: typeof DEFI_ASSETS,
  moduleName: string,
  options?: {
    allowList?: string[];
    requireNonEmptyAllowList?: boolean;
  },
) {
  const { allowList = [], requireNonEmptyAllowList = false } = options ?? {};

  return ChainAllowBlockListSchema.default({
    allowList: [...allowList],
    blockList: [],
  }).superRefine((data, ctx) => {
    if (requireNonEmptyAllowList && data.allowList.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["allowList"],
        message: `${moduleName} module chains.allowList must contain at least one chain ID`,
      });
      return;
    }

    // If no restrictions, skip validation (empty allowList = all chains allowed)
    if (data.allowList.length === 0) {
      return;
    }

    // If module has no asset restrictions (empty array), any chain is allowed
    if (moduleAssets.length === 0) {
      return;
    }

    // Get supported chain IDs from module assets
    const supportedChainIds = getSupportedChainIds(moduleAssets);

    // Normalize both lists for comparison

    // Check if all chains in allowList are supported
    const unsupportedChains = data.allowList.filter(
      (chainId) => !supportedChainIds.includes(chainId),
    );

    if (unsupportedChains.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: `${moduleName} module does not support chain ID(s): ${unsupportedChains.join(", ")}. Supported chains: ${supportedChainIds.join(", ")}`,
      });
    }
  });
}

/**
 * Asset-specific schema for allow/block list filters with chainId and address
 */
const AssetAllowBlockListSchema = z.object({
  allowList: z.array(AssetConfigSchema).default([]),
  blockList: z.array(AssetConfigSchema).default([]),
});

// Swap chains allowlist uses CAIP-2 identifiers from asset config (e.g., "eip155:1")
export const SWAP_CHAIN_ALLOWLIST = Array.from(
  new Set(
    SWAP_ASSETS.flatMap((asset) => asset.networks.map((network) => String(network.caipNetworkId))),
  ),
);

const swapChainIdEnum = z.enum(SWAP_CHAIN_ALLOWLIST as [string, ...string[]]);

const DEFAULT_SWAP_CHAIN_FILTERS = {
  allowList: [...SWAP_CHAIN_ALLOWLIST],
  blockList: [] as string[],
};

const SwapChainsSchema = z
  .object({
    allowList: z
      .array(swapChainIdEnum)
      .nonempty({ message: "Swap chains.allowList must contain at least one chain ID" })
      .default([...SWAP_CHAIN_ALLOWLIST]),
    blockList: z.array(swapChainIdEnum).default([]),
  })
  .default(DEFAULT_SWAP_CHAIN_FILTERS);

/**
 * Swap-specific feature schema extending the base feature with allow/block lists.
 */
const SwapFeatureSchema = BaseFeatureSchema.extend({
  // Chains allow/block lists using CAIP-2 chain IDs (validated against SWAP_ASSETS)
  chains: SwapChainsSchema,
  assets: AssetAllowBlockListSchema.default({ allowList: [], blockList: [] }).optional(),
  dexes: AllowBlockListSchema.default({ allowList: [], blockList: [] }).optional(),
  bridges: AllowBlockListSchema.default({ allowList: [], blockList: [] }).optional(),
  fees: z
    .object({
      enabled: z.boolean(),
    })
    .default({ enabled: false }),
}).strict();

/**
 * DeFi-specific feature schema extending the base feature with allow/block lists
 * for chains and assets.
 *
 * IMPORTANT: When defi.enabled is true, assets.allowList must contain at least one asset.
 * This is required because the BD SDK needs to know which assets to fetch pools for.
 */
const DefiFeatureSchema = BaseFeatureSchema.extend({
  // Required protocol markets for DeFi - at least one market must be specified when enabled
  markets: LendBorrowMarkets.default([]),
  // Chains allow/block lists accept CAIP-2 IDs (validated against DEFI_ASSETS)
  chains: createModuleChainSchema(DEFI_ASSETS, "DeFi").optional(),
  // Assets allow/block lists using CAIP-2 chain IDs and checksummed addresses
  // Required when defi is enabled - must have at least one asset in allowList
  assets: AssetAllowBlockListSchema.default({ allowList: [], blockList: [] }),
})
  .strict()
  .superRefine((data, ctx) => {
    // Validate markets is provided and non-empty when defi is enabled
    if (data.enabled && (!data.markets || data.markets.length === 0)) {
      ctx.addIssue({
        code: "custom",
        path: ["markets"],
        message: "At least one market must be specified when DeFi is enabled",
      });
    }

    // Require assets.allowList when defi is enabled
    if (data.enabled && (!data.assets?.allowList || data.assets.allowList.length === 0)) {
      ctx.addIssue({
        code: "custom",
        path: ["assets", "allowList"],
        message:
          "DeFi feature requires at least one asset in assets.allowList when enabled. Example: { chainId: 'eip155:1', address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48' }",
      });
      return;
    }

    // Only validate chain/asset consistency if both are present
    if (!data.chains || !data.assets) {
      return;
    }

    // Validate that asset chain IDs are consistent with chains allowList
    const chainsAllowList = data.chains.allowList;
    const assetsAllowList = data.assets.allowList;

    // If no chain restrictions, all asset chains are allowed
    if (chainsAllowList.length === 0) {
      return;
    }

    // Normalize chain IDs for comparison

    // Find any asset chain IDs that aren't in the chains allowList
    const inconsistentAssets = assetsAllowList.filter((asset) => {
      return !chainsAllowList.includes(asset.chainId);
    });

    if (inconsistentAssets.length > 0) {
      const chainIds = inconsistentAssets.map((a) => a.chainId).join(", ");
      ctx.addIssue({
        code: "custom",
        path: ["assets"],
        message: `Asset chain IDs [${chainIds}] are not in the chains allowList. All asset chain IDs must be present in chains.allowList or chains.allowList must be empty.`,
      });
    }
  });

/**
 * Zod schema for Solana vote pubkey with validation
 */
const SolanaVotePubKeySchema = z.string().refine(validateSolanaVotePubKey, {
  message: "Must be a valid Solana vote pubkey (base58 encoded, 32 bytes, on ed25519 curve)",
});

/**
 * A single Solana validator address entry pairing a CAIP-2 chain ID with a vote pubkey.
 */
const SolanaValidatorAddressSchema = z.object({
  chainId: z.string(),
  address: SolanaVotePubKeySchema,
});

/**
 * Solana-specific staking configuration
 */
const SolanaStakingConfigSchema = z
  .object({
    enabled: z.boolean().default(true),
    validatorAddresses: z
      .array(SolanaValidatorAddressSchema)
      .nonempty({ message: "Solana validatorAddresses must contain at least one entry" })
      .optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.enabled && !data.validatorAddresses) {
      ctx.addIssue({
        code: "custom",
        path: ["validatorAddresses"],
        message: "Solana validatorAddresses are required when staking.solana.enabled is true",
      });
    }
  });

const EthereumVaultAddressSchema = z.object({
  chainId: z.string(),
  address: z
    .string()
    .regex(/^0x[0-9a-fA-F]{40}$/, "Must be a valid EVM address (0x + 40 hex chars)"),
});

/**
 * Ethereum-specific staking configuration
 */
const EthereumStakingConfigSchema = z
  .object({
    enabled: z.boolean().default(true),
    referralTag: z.string().optional(),
    vaultAddresses: z
      .array(EthereumVaultAddressSchema)
      .nonempty({ message: "Ethereum vaultAddresses must contain at least one entry" })
      .optional(),
  })
  .strict();

/**
 * Staking-specific feature schema extending the base feature with chain restrictions
 */
const StakingFeatureSchema = BaseFeatureSchema.extend({
  // Chains allow/block lists accept CAIP-2 IDs (validated against STAKING_ASSETS)
  chains: createModuleChainSchema(STAKING_ASSETS, "Staking").optional(),
  ethereum: EthereumStakingConfigSchema.optional(),
  // Solana-specific staking configuration
  solana: SolanaStakingConfigSchema.optional(),
}).strict();

/**
 * Wraps a feature schema to also accept `false` as a shorthand for `{ enabled: false }`.
 * Used for the input schema (JSON schema generation) where transforms are not allowed.
 */
function featureOrFalse<T extends z.ZodType>(schema: T) {
  return z.union([z.literal(false), schema]);
}

/**
 * Batch transaction options — shared by swap and defi features.
 * Controls how multi-call transactions (fee + approval + swap) are batched.
 * Exactly one of eip5792 or eip7702 must be set when batchOption is present; never both.
 * (JSON keys stay lowercase; app code uses canonical `EIP-5792` / `EIP-7702` — see `@blockdaemon/widget-host` `BatchMethod`.)
 *
 * @example Valid — EIP-5792 only (wallet-side batching)
 * batchOption: { eip5792: { atomicRequired: false } }
 * batchOption: { eip5792: { atomicRequired: true } }
 *
 * @example Valid — EIP-7702 only (batcher contract)
 * batchOption: { eip7702: { batcherContract: "0xA1fdd934a977898B72ddBFbeC5525e266b7e9991" } }
 *
 * @example Invalid — both set (rejected)
 * batchOption: { eip5792: { atomicRequired: true }, eip7702: { batcherContract: "0x..." } }
 *
 * @example Invalid — neither set (rejected when batchOption is present)
 * batchOption: {}
 *
 * @see https://eips.ethereum.org/EIPS/eip-5792
 * @see https://eips.ethereum.org/EIPS/eip-7702
 */
const BatchOptionSchema = z
  .object({
    eip5792: z
      .object({
        atomicRequired: z.boolean().default(false),
      })
      .optional(),
    eip7702: z
      .object({
        batcherContract: EthereumAddressSchema,
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const has5792 = "eip5792" in data && data.eip5792 != null;
    const has7702 = "eip7702" in data && data.eip7702 != null;

    if (has5792 && has7702) {
      ctx.addIssue({
        code: "custom",
        path: ["eip5792", "eip7702"],
        message: "Exactly one of eip5792 or eip7702 must be set, not both",
      });
    }
    if (!has5792 && !has7702) {
      ctx.addIssue({
        code: "custom",
        path: [],
        message: "Exactly one of eip5792 or eip7702 must be set",
      });
    }
  });

/**
 * Input schema for widget features: accepts `false` as a shorthand for disabled features.
 * This schema is used for JSON schema generation (via z.toJSONSchema) because transforms
 * cannot be represented in JSON Schema format.
 *
 * @example
 * // Both forms are valid input:
 * { "defi": false }
 * { "defi": { "enabled": false } }
 */
const WidgetFeaturesInputSchema = z
  .object({
    staking: featureOrFalse(StakingFeatureSchema),
    swap: featureOrFalse(SwapFeatureSchema),
    defi: featureOrFalse(DefiFeatureSchema),
    // either eip5792 object or eip7702 object or nothing if off
    batchOption: BatchOptionSchema.optional(),
  })
  .extend(WalletFeatureFieldsObjectSchema.shape)
  .superRefine((data, ctx) => {
    refineWalletBalanceMode(data, ctx);
  });

/**
 * Normalizes parsed features input by converting any `false` shorthand
 * to the full disabled object form with all defaults applied.
 */
function normalizeFeatureInput(features: z.infer<typeof WidgetFeaturesInputSchema>) {
  return {
    ...features,
    staking:
      features.staking === false
        ? StakingFeatureSchema.parse({ enabled: false })
        : features.staking,
    swap: features.swap === false ? SwapFeatureSchema.parse({ enabled: false }) : features.swap,
    defi: features.defi === false ? DefiFeatureSchema.parse({ enabled: false }) : features.defi,
  };
}

/**
 * Runtime schema for widget features: parses input (accepting `false` shorthand)
 * and transforms it so the output is always the full object form.
 */
export const WidgetFeaturesSchema = WidgetFeaturesInputSchema.transform(normalizeFeatureInput);

/**
 * Main widget configuration schema
 */
export const WidgetConfigSchema = z.object({
  widgetType: z.literal("earn").optional().default("earn"),
  features: WidgetFeaturesSchema,
  appearance: WidgetAppearanceSchema,
});

/**
 * Input-only schema for JSON schema generation (no transforms).
 * Use this with z.toJSONSchema() since transforms cannot be represented in JSON Schema.
 * Accepts the same inputs as WidgetConfigSchema but does not normalize false → object.
 */
export const WidgetConfigInputSchema = z.object({
  widgetType: z.literal("earn").optional().default("earn"),
  features: WidgetFeaturesInputSchema,
  appearance: WidgetAppearanceSchema,
});

/**
 * Refined schema with additional business logic validation
 */
export const ValidatedWidgetConfigSchema = WidgetConfigSchema.superRefine((config, ctx) => {
  // Ensure at least one core page feature is enabled (exclude walletMode and walletInfo)
  const enabledCoreFeatures = [
    config.features.staking.enabled,
    config.features.swap.enabled,
    config.features.defi.enabled,
  ].filter(Boolean);

  if (enabledCoreFeatures.length === 0) {
    ctx.addIssue({
      code: "custom",
      message: "At least one feature (staking, swap, or defi) must be enabled",
      path: ["features"],
    });
  }

  // External mode is host-driven and does not render WalletInfo in the widget UI.
  if (config.features.walletMode === "external" && config.features.walletInfo) {
    ctx.addIssue({
      code: "custom",
      message: "walletInfo must be false when walletMode is external",
      path: ["features", "walletInfo"],
    });
  }
});

// Export types inferred from schemas
export type WidgetFeatures = z.infer<typeof WidgetFeaturesSchema>;
export type WidgetAppearance = z.infer<typeof WidgetAppearanceSchema>;
export type WidgetConfig = z.infer<typeof WidgetConfigSchema>;
export type ValidatedWidgetConfig = z.infer<typeof ValidatedWidgetConfigSchema>;

/**
 * Normalized feature flags for UI consumption: always booleans for feature pages,
 * while keeping wallet fields as-is. This allows the rest of the app to treat
 * features as simple flags even if the config uses object forms.
 */
export type NormalizedWidgetFeatures = {
  staking: boolean;
  swap: boolean;
  defi: boolean;
  walletInfo: boolean;
  walletMode: "internal" | "external";
  balanceMode: "internal" | "external";
};

export function normalizeFeatures(features: WidgetFeatures): NormalizedWidgetFeatures {
  const get = (value: { enabled: boolean }): boolean => Boolean(value?.enabled);

  return {
    staking: get(features.staking),
    swap: get(features.swap),
    defi: get(features.defi),
    walletInfo: features.walletMode === "external" ? false : Boolean(features.walletInfo),
    walletMode: features.walletMode,
    balanceMode: features.balanceMode,
  };
}

/**
 * Validates a customer config object against the schema
 */
export function validateCustomerConfig(config: unknown): ValidatedWidgetConfig {
  return parseWithCustomerConfigMessage(
    ValidatedWidgetConfigSchema,
    config,
    "Invalid customer config",
  );
}

/**
 * Safely validates a config and returns result with error information
 */
export function safeValidateCustomerConfig(config: unknown): {
  success: boolean;
  data?: ValidatedWidgetConfig;
  error?: string;
} {
  try {
    const validatedConfig = validateCustomerConfig(config);
    return { success: true, data: validatedConfig };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown validation error",
    };
  }
}
